import PropTypes from 'prop-types';
import React, { useContext, useState, useMemo, useCallback } from 'react';
import { useTranslation } from "react-i18next";
import { useDispatch } from 'react-redux';
import { LoadingContext, M_StateActions, MTable } from 'src/M_Components';

export default function MarketConfig({ roles, onCreateTab, toastMessages }) {
    const ctx = useContext(LoadingContext);
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const [refresh, setRefresh] = useState(false);

    // Keep this list stable across renders to avoid unnecessary child rerenders
    const removeContextItems = useMemo(() => ['Print', 'Copy'], []);

    // Centralize CRUD route to avoid hard-coded duplicates
    const CRUD_ROUTE = '/home/markets/marketConfigCrud';

  
    //====================================================================================================
    // openMarketCrud:
    // - Stable (useCallback) handler to open/focus the Market CRUD tab.
    // - Persists 'operation' in global state; for View/Edit/Copy also persists the selected row.
    // - Validates presence of a row when required and shows a localized toast if missing.
    // - Builds the tab label using i18n and the market's Name.
    // - Using a stable callback avoids MTable refetches caused by changing prop identities.
    //====================================================================================================
    const openMarketCrud = useCallback(
        (operation, row) => {
            // Persist operation in global state
            dispatch(M_StateActions.addM_State({ key: 'operation', value: operation }));

            // For the operations below, must be provided row/entity data
            if (['Edit', 'Copy', 'View'].includes(operation)) {
                if (!row) {
                    toastMessages('error', '', t('SelectedLine')); //If no data for this operations, show na error
                    return;
                }
                //Persist the selected Market line data as a row
                dispatch(M_StateActions.addM_State({ key: 'MarketLine', value: row }));
            }

            // Build the tab label name
            const tabLabel =
                operation === 'Create'
                    ? t('LabelNewMarket')
                    : operation === 'View'
                    ? `${t('LabelViewMarket')} "${row?.Name ?? ''}"`
                    : `${t('LabelEditMarket')} "${row?.Name ?? ''}"`;

            onCreateTab(CRUD_ROUTE, tabLabel);
        },
        [dispatch, onCreateTab, t, toastMessages]
    );


    //===============================================================================================
    // replaceFunctions:
    // - Memoized map of context actions consumed by MTable.
    // - useMemo keeps the Map identity stable across renders, preventing unnecessary rerenders
    //   and accidental duplicate fetches inside MTable.
    //===============================================================================================
    const replaceFunctions = useMemo(
        () =>
            new Map([
                ['View', (row) => openMarketCrud('View', row)],
                ['Create', () => openMarketCrud('Create')],
                ['Edit', (row) => openMarketCrud('Edit', row)],
            ]),
        [openMarketCrud]
    );

    return (
        <div className='divGlobalSettings'>
            <div className='subTitleSettings'>
                <div>
                    <div className="titleSettings" style={{ color: ctx.secondaryColor }}>{t('Markets')}</div>
                    <div className='labelSubtitleSettings'>{t("InfoMarkets")}</div>
                </div>
            </div>
            <MTable // Main table component for displaying market data in a dynamic table
                filtersOnTable
                entityName={'Markets'}
                roleName={'Markets'} 
                rolePermissions={roles?.map(vl => vl.Value)} // User roles for permission management
                removeContextItems={removeContextItems}
                refresh={refresh}
                setRefresh={setRefresh}
                useSessionStorage={false}
                replaceFunctions={replaceFunctions} // Custom action handlers
            />
        </div>
    );
}

MarketConfig.propTypes = {
    roles: PropTypes.array,
    onCreateTab: PropTypes.func,
    toastMessages: PropTypes.func
};
